import { useAuth } from "context/AuthContext";
import { routesPath } from "conts/routes";
import { useInstructorWelcomeModal } from "hooks/useInstructorWelcomeModal";
import { useOutsideClick } from "hooks/useOutsideClick";
import { useWelcomeModal } from "hooks/useWelcomeModal";
import { appRoute } from "pages/app/app.routes";
import { instructorRoute } from "pages/instructor/instructor.routes";
import { userRoute } from "pages/user/user.routes";
import { useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ForgotPasswordModal } from "ui/blocks/AuthModals/forgotPasswordModal/forgotPasswordModal";
import { LoginModal } from "ui/blocks/AuthModals/loginModal/LoginModal";
import { SignupModal } from "ui/blocks/AuthModals/singupModal/SignupModal";
import { InstructorWelcomeModal } from "ui/blocks/InstructorWelcomeModal/InstructorWelcomeModal";
import { WelcomeModal } from "ui/blocks/WelcomeModal/WelcomeModal";
import { MenuIcon } from "ui/icons";
import { defaultThemeColors, themeColor } from "ui/theme";
import { pixelTrackEvent } from "utils/facebookPixel";

export const Dropdown = ({
  isLoggedIn,
  onLogout,
  userRole,
  userData,
  unreadNotificationCount,
  unreadMessagesCount,
  primary,
  modalState: [showSignupModal, setShowSignupModal],
  forgotPaswordModalState: [showForgotPasswordModal, setForgotPasswordModal],
  signInModalState: [showModal, setShowModal],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isOpen: isOpenModal, closeModal } = useWelcomeModal();
  const { isOpen: isInstructorModalOpen, closeModal: closeInstructorModal } = useInstructorWelcomeModal();
  const { token, toggleRole } = useAuth();

  const isMobile = useMediaQuery({ query: "(max-width: 1024px)" });

  const isInReview = userData?.teacher?.status === "awaiting";

  const navigate = useNavigate();
  const menuRef = useRef();

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };
  const closeDropdown = () => {
    setIsOpen(false);
  };

  const onSwitch = (to) => {
    toggleRole();
    navigate(to);
  };

  const getSwitchRoleMenuItem = () => {
    if (!isMobile) return null;

    if (token) {
      if (userRole === 1) {
        return userData?.teacher?.profile_completion === "100"
          ? {
              label: isInReview ? "Listing in review" : "Switch to Teach",
              action: () => {
                if (!isInReview) onSwitch(instructorRoute.homeInstructor);
              },
            }
          : {
              label: "Become an Instructor",
              action: () => navigate(appRoute.completeProfile.fullPath),
            };
      } else {
        return {
          label: "Switch to learn",
          action: () => onSwitch("/"),
        };
      }
    } else {
      return {
        label: "Become an Instructor",
        action: () => setShowSignupModal(true),
      };
    }
  };

  useOutsideClick(menuRef, closeDropdown);

  const menuItems = [
    {
      label: "Log in",
      action: () => setShowModal(true),
    },
    {
      label: "Sign up",
      action: () => setShowSignupModal(true),
    },
    { separator: true },
    { label: "About Us", action: () => navigate(routesPath.about) },
    { label: "FAQ's", action: () => navigate(routesPath.faq) },
    { label: "Legal", action: () => navigate(routesPath.terms) },
    {
      label: "Become an instructor",
      action: () => setShowSignupModal(true),
    },
    {
      label: "Contact us",
      action: () => {
        if (window.Intercom) {
          window.Intercom("show");
        }
      },
    },
  ];

  const userMenu = [
    {
      label: "Classes",
      action: () => {
        navigate(userRoute.myClasses);
        pixelTrackEvent("Home-header: Clicked on My Classes");
      },
    },
    {
      label: "Messages",
      action: () => navigate(userRoute.messages),
    },
    {
      label: "Notifications",
      action: () => navigate(userRoute.notifications),
    },
    { separator: true },
    getSwitchRoleMenuItem(),
    { label: "Settings", action: () => navigate(userRoute.settings) },
    { label: "Legal", action: () => navigate(routesPath.terms) },
    {
      label: "Logout",
      action: onLogout,
    },
  ].filter((item) => item !== null);

  const instructorMenu = [
    {
      label: "Notifications",
      action: () => navigate(instructorRoute.notifications),
    },
    {
      label: "Class listing",
      action: () => navigate(instructorRoute.classListing),
    },
    { separator: true },
    getSwitchRoleMenuItem(),
    { label: "Settings", action: () => navigate(instructorRoute.settings) },
    {
      label: "Contact us",
      action: () => {
        if (window.Intercom) {
          window.Intercom("show");
        }
      },
    },
    { label: "Legal" },
    {
      label: "Logout",
      action: onLogout,
    },
  ].filter((item) => item !== null);

  const menus = isLoggedIn
    ? userRole === 1
      ? userMenu
      : instructorMenu
    : menuItems;

  const profilePic =
    userRole === 1 ? userData?.profile_pic : userData?.teacher?.profile_pic;

  return (
    <>
      {showSignupModal ? (
        <SignupModal
          show={showSignupModal}
          handleClose={() => setShowSignupModal(false)}
          setLoginModal={setShowModal}
        />
      ) : null}

      {showForgotPasswordModal ? (
        <ForgotPasswordModal
          show={showForgotPasswordModal}
          handleClose={() => setForgotPasswordModal(false)}
          setSigninModal={setShowModal}
        />
      ) : null}

      {showModal ? (
        <LoginModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          setSignupModal={setShowSignupModal}
          setForgotPasswordModal={setForgotPasswordModal}
        />
      ) : null}

      {isOpenModal ? (
        <WelcomeModal show={isOpenModal} handleClose={closeModal} />
      ) : null}

      {isInstructorModalOpen ? (
        <InstructorWelcomeModal show={isInstructorModalOpen} handleClose={closeInstructorModal} />
      ) : null}

      <DropdownContainer ref={menuRef}>
        <AvatarWrapper primary={primary}>
          <DropdownActionButton onClick={toggleDropdown} aria-label="Open menu">
            <MenuIcon className="menu-icon" style={{ width: "20px" }} />
          </DropdownActionButton>
        </AvatarWrapper>

        {isOpen && (
          <DropdownMenu>
            {menus.map((item, index) =>
              item.separator ? (
                <Separator key={`separator-${index}`} />
              ) : (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    closeDropdown();
                    item.action?.();
                  }}
                >
                  {item.label}

                  {item.label === "Notifications" && unreadNotificationCount ? (
                    <BadgeCount>{unreadNotificationCount}</BadgeCount>
                  ) : null}

                  {item.label === "Messages" && unreadMessagesCount ? (
                    <BadgeCount>{unreadMessagesCount}</BadgeCount>
                  ) : null}
                </MenuItem>
              ),
            )}
          </DropdownMenu>
        )}
      </DropdownContainer>
    </>
  );
};

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const AvatarWrapper = styled.div`
  padding-inline: 14px;
  gap: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  @media (max-width: 1023px) {
    padding-right: 0;
  }
  @media (max-width: 400px) {
    gap: 4px;
    padding: 0; 
  }

  border-radius: 99rem;

  & > svg {
    height: 20px;
    width: 20px;

    @media (max-width: 1023px) {
      color: ${({ primary }) =>
        primary ? defaultThemeColors.white : defaultThemeColors.blue};
    }
  }
`;

const Separator = styled.div`
  height: 0.5px;
  width: 100%;
  background-color: ${themeColor("txt")};
  opacity: 0.2;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border: 1px solid rgb(0 0 0 / 0.05);
  border-radius: 8px;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  margin-top: 8px;
  min-width: 220px;
  overflow: hidden;
  z-index: 10;
`;

const MenuItem = styled.a`
  display: block;
  position: relative;
  flex-direction: row;
  padding: 12px 16px;
  text-decoration: none;
  color: ${themeColor("txt")};
  font-size: 14px;
  white-space: nowrap;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: ${themeColor("bgNeutral")};
  }
`;

const BadgeCount = styled.div`
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  bottom: 0.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  font-size: 0.75rem;
  font-weight: bold;
  color: white;
  background-color: #f56565;
  border: 2px solid white;
  border-radius: 9999px;
`;

const ProfileBadge = styled.div`
  position: absolute;
  top: 0.2rem;
  right: 0.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0px;
  height: 0px;
  font-weight: bold;
  color: white;
  background-color: #f56565;
  border: 2px solid white;
  border-radius: 9999px;

  @media (min-width: 1024px) {
    width: 1rem;
    height: 1rem;
  }
`;

const DropdownActionButton = styled.button`
  height: 42px;
  width: 42px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.basicBlack};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  transition: background-color 0.2s ease;
  cursor: pointer;
  border: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.blue};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    background-color: ${({ theme }) => theme.colors.blue};
    outline: 2px solid ${({ theme }) => theme.colors.blue};
    outline-offset: 3px;
  }
`;
